import styled from 'styled-components';
import { Collapse, Typography } from '@mui/material';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import { TimelineEventLabel } from 'incidents/components/TimelineEventLabel';
import type { FetchTeamUsersApi } from 'shared/types';
import type { EventGroup, IncidentDetailEvent } from '../../types';
import { formatDate, getEventIcon, getEventLink } from '../utils';

const EventRow = styled(Flex)`
  gap: 12px;
  align-items: center;
`;

const IconWrapper = styled(Flex)`
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LineWrapper = styled(Flex)`
  width: 24px;
  justify-content: center;
  flex-shrink: 0;
`;

const VerticalLine = styled.div`
  width: 1px;
  height: 12px;
  background-color: ${({ theme }) => theme.palette.common.gray5};
`;

const ShowMoreLineWrapper = styled(Flex)`
  width: 24px;
  justify-content: center;
  flex-shrink: 0;
`;

const ShowMoreLine = styled.div`
  width: 1px;
  background-color: ${({ theme }) => theme.palette.common.gray5};
`;

const EventContent = styled(Flex)`
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span`
  color: ${({ theme }) => theme.palette.common.gray4};
  font-size: 14px;
`;

const EventTime = styled(Typography)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray4};
  white-space: nowrap;
`;

const ShowMoreLink = styled(Typography)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray4};
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
`;

const ExpandedSection = styled(Flex)`
  gap: 12px;
  align-items: stretch;
`;

const ExpandedContentWrapper = styled(FlexColumn)`
  flex: 1;
  gap: 8px;
  min-width: 0;
`;

const ExpandedEventsBox = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.common.gray15};
  border-radius: 8px;
  padding: 8px;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
`;

const ExpandedEventItem = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const DotComponent = () => <Dot>&middot;</Dot>;

type Props = {
  group: EventGroup;
  isExpanded: boolean;
  isLastGroup: boolean;
  onToggle: () => void;
  usersData?: FetchTeamUsersApi;
  onOpenIncident?: (incidentId: string) => void;
};

const TimelineEventGroup = ({
  group,
  isLastGroup,
  onToggle,
  isExpanded,
  usersData,
  onOpenIncident,
}: Props) => {
  const hasMultiple = group.events.length > 1;
  const firstEvent = group.events[0];
  const restEvents = group.events.slice(1);
  const firstDate = formatDate(group.firstEvent.eventTime);
  const lastDate = formatDate(group.lastEvent.eventTime);
  const dateDisplay = hasMultiple ? `${lastDate} - ${firstDate}` : firstDate;

  return (
    <>
      <EventRow>
        <IconWrapper>{getEventIcon(firstEvent, usersData)}</IconWrapper>
        <EventContent>
          <TimelineEventLabel label={group.label} link={group.link} />
          <DotComponent />
          <EventTime>{dateDisplay}</EventTime>
        </EventContent>
      </EventRow>
      {hasMultiple && (
        <ExpandedSection>
          {!isLastGroup && (
            <ShowMoreLineWrapper>
              <ShowMoreLine />
            </ShowMoreLineWrapper>
          )}
          <ExpandedContentWrapper sx={{ marginLeft: isLastGroup ? '36px' : 0 }}>
            <ShowMoreLink onClick={onToggle}>
              {isExpanded
                ? `Hide ${group.events.length} events`
                : `Show all ${group.events.length} events`}
            </ShowMoreLink>
            <Collapse in={isExpanded} timeout={200}>
              {restEvents.length > 0 && (
                <ExpandedEventsBox>
                  {restEvents.map((event: IncidentDetailEvent) => (
                    <ExpandedEventItem key={event.id}>
                      <TimelineEventLabel
                        label={group.label}
                        link={getEventLink(event, usersData, {
                          onOpenIncident,
                        })}
                      />
                      <DotComponent />
                      <EventTime>{formatDate(event.eventTime)}</EventTime>
                    </ExpandedEventItem>
                  ))}
                </ExpandedEventsBox>
              )}
            </Collapse>
          </ExpandedContentWrapper>
        </ExpandedSection>
      )}
      {!isLastGroup && (
        <LineWrapper>
          <VerticalLine />
        </LineWrapper>
      )}
    </>
  );
};

export default TimelineEventGroup;
